import React from 'react';
import moment from 'moment';
import {
  unitSymbol,
  resolutionLabel,
  horizonCommitment,
  freshness,
  coveragePercent,
  spanLabel,
} from '../helpers/marketProfile';
import {getLocalTimezoneLabel} from '../utils/datetime';

const Cap = ({children}) => (
  <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500 dark:text-gray-400">
    {children}
  </span>
);

const Chip = ({children}) => (
  <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10.5px] text-slate-600 dark:bg-gray-700/60 dark:text-gray-300">
    {children}
  </span>
);

const Tile = ({label, value, sub, tone = 'ink', className = '', children}) => (
  <div className={className}>
    <div className="text-[9.5px] font-semibold uppercase tracking-[0.11em] text-slate-500 dark:text-gray-400">
      {label}
    </div>
    <div
      className={`mt-0.5 font-mono text-lg font-semibold tabular-nums tracking-tight ${
        tone === 'warn'
          ? 'text-amber-700 dark:text-amber-400'
          : 'text-slate-900 dark:text-white'
      }`}
    >
      {value}
    </div>
    {children}
    {sub && (
      <div className="mt-0.5 text-[11.5px] leading-snug text-slate-500 dark:text-gray-400">
        {sub}
      </div>
    )}
  </div>
);

const CoverageBar = ({percent}) => (
  <div className="mt-1.5 flex h-[5px] overflow-hidden rounded-full bg-slate-200 dark:bg-gray-700">
    <span className="block bg-brand-600 dark:bg-brand-400" style={{width: `${percent}%`}}/>
    <span className="block bg-amber-500/80" style={{width: `${100 - percent}%`}}/>
  </div>
);

// One-line description of how far behind the measurements are, phrased in days
// rather than intervals so it reads at a glance.
export const freshnessLabel = (state, latest) => {
  if (state === 'fresh') return 'measurements current';
  if (state === 'unknown') return 'no measurements yet';
  return `measurements ${moment(latest).fromNow(true)} behind`;
};

/**
 * Expanded body of a resource card for a signed-in forecaster: what the target
 * is, how much history there is to train on, and what the challenges ask for.
 * Prose on the left, every measured figure in the rail on the right.
 */
export default function ResourceDetails({resource: r, programUrl, onCopyRequest, onCopyId}) {
  const profile = r.profile || {};
  const mm = r.measurements_metadata || {};
  const horizons = r.program_summary?.horizons || [];
  const unit = unitSymbol(profile.unit);
  const resolution = resolutionLabel(profile.resolution_minutes);
  const facts = [unit, resolution, profile.timezone].filter(Boolean);
  const {state} = freshness(mm.end_datetime, profile.resolution_minutes);
  const coverage = coveragePercent(mm, profile.resolution_minutes);
  const span = spanLabel(mm);
  const samples = parseInt(mm.n_samples, 10);
  const request = `GET /api/v1/data/raw-measurements/?resource=${r.id}`;

  const budgets = [
    ['intraday', r.monthly_budget_intraday],
    ['D+1', r.monthly_budget_d1],
    ['D+N', r.monthly_budget_dn],
  ]
    .map(([label, value]) => [label, parseFloat(value)])
    .filter(([, value]) => Number.isFinite(value) && value > 0)
    .map(([label, value]) => `${label} €${value.toLocaleString()}`)
    .join(' · ');

  return (
    <div data-testid="resource-details">
      <div className="grid border-t border-slate-900/[0.06] dark:border-gray-100/[0.07] lg:grid-cols-[minmax(0,1fr)_268px]">
        {/* Prose column */}
        <div className="flex min-w-0 flex-col gap-5 px-3 pb-4 pt-3.5 lg:px-5 lg:pb-5 lg:pt-4">
          <section className="flex flex-col gap-2.5">
            <Cap>Forecast target</Cap>
            {profile.target_summary ? (
              <p className="max-w-[48ch] text-[14.5px] leading-snug text-slate-900 dark:text-white">
                {profile.target_summary}
              </p>
            ) : (
              <p className="max-w-[48ch] text-[13px] italic text-slate-500 dark:text-gray-400">
                The market owner has not published a target description yet.
              </p>
            )}
            {facts.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {facts.map(f => <Chip key={f}>{f}</Chip>)}
              </div>
            )}
            {profile.source_name && (
              <p className="max-w-[52ch] text-[13px] leading-relaxed text-slate-700 dark:text-gray-300">
                Scored against measurements published by{' '}
                <span className="font-semibold text-slate-900 dark:text-white">
                  {profile.source_name}
                </span>
                , a public dataset you can also train on.
                {profile.source_url && (
                  <>
                    {' '}
                    <a
                      href={profile.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-brand-600 hover:underline underline-offset-4 dark:text-brand-400"
                    >
                      Open the dataset ↗
                    </a>
                  </>
                )}
              </p>
            )}
            {profile.target_note && (
              <p className="max-w-[52ch] text-[12.5px] leading-relaxed text-slate-500 dark:text-gray-400">
                {profile.target_note}
              </p>
            )}
          </section>

          <section className="flex flex-col gap-2.5 border-t border-slate-900/[0.06] pt-4 dark:border-gray-100/[0.07]">
            <Cap>Challenges &amp; scoring</Cap>
            {horizons.length > 0 ? (
              <dl className="grid grid-cols-[auto_1fr] gap-x-3.5 gap-y-1.5 text-[13px]">
                {horizons.map((h) => {
                  const {label, detail} = horizonCommitment(h);
                  return (
                    <React.Fragment key={h.label}>
                      <dt className="whitespace-nowrap font-semibold text-slate-900 dark:text-white">
                        {label}
                      </dt>
                      <dd className="m-0 text-slate-500 dark:text-gray-400">{detail}</dd>
                    </React.Fragment>
                  );
                })}
                <dt className="whitespace-nowrap text-slate-500 dark:text-gray-400">You submit</dt>
                <dd className="m-0 text-slate-700 dark:text-gray-300">
                  <span className="font-semibold text-slate-900 dark:text-white">Q10, Q50, Q90</span>
                  {' '}per timestamp
                </dd>
                <dt className="whitespace-nowrap text-slate-500 dark:text-gray-400">Deterministic</dt>
                <dd className="m-0 text-slate-700 dark:text-gray-300">
                  Q50 against the measurement, as{' '}
                  <span className="font-semibold text-slate-900 dark:text-white">RMSE</span>
                </dd>
                <dt className="whitespace-nowrap text-slate-500 dark:text-gray-400">Probabilistic</dt>
                <dd className="m-0 text-slate-700 dark:text-gray-300">
                  the Q10–Q90 interval, as{' '}
                  <span className="font-semibold text-slate-900 dark:text-white">MWI</span>
                </dd>
                {budgets && (
                  <>
                    <dt className="whitespace-nowrap text-slate-500 dark:text-gray-400">Reward split</dt>
                    <dd className="m-0 text-slate-700 dark:text-gray-300">{budgets}</dd>
                  </>
                )}
              </dl>
            ) : (
              <p className="max-w-[52ch] text-[13px] leading-relaxed text-slate-500 dark:text-gray-400">
                No paid challenges are published for this resource yet, so there is
                nothing to submit against it today.
              </p>
            )}
            <div className="flex flex-wrap gap-x-4 gap-y-1.5 pt-0.5">
              <a
                href="/schedule"
                onClick={(e) => e.stopPropagation()}
                className="text-[12.5px] text-brand-600 hover:underline underline-offset-4 dark:text-brand-400"
              >
                Upcoming gate closures ↗
              </a>
              {programUrl && (
                <a
                  href={programUrl}
                  onClick={(e) => e.stopPropagation()}
                  className="text-[12.5px] text-brand-600 hover:underline underline-offset-4 dark:text-brand-400"
                >
                  Program rules ↗
                </a>
              )}
            </div>
          </section>
        </div>

        {/* Numbers rail */}
        <aside className="grid grid-cols-2 gap-4 border-t border-slate-900/[0.06] px-3 py-4 dark:border-gray-100/[0.07] lg:flex lg:flex-col lg:border-l lg:border-t-0 lg:bg-slate-50/80 lg:px-5 lg:dark:bg-gray-100/[0.03]">
          <Tile
            label="history"
            value={Number.isFinite(samples) ? samples.toLocaleString() : '—'}
            sub={[
              'samples',
              span && resolution ? `${span} at ${resolution}` : null,
            ].filter(Boolean).join(' · ')}
          />
          <Tile
            label="latest measurement"
            value={mm.end_datetime ? moment(mm.end_datetime).fromNow(true) : '—'}
            tone={state === 'fresh' || state === 'unknown' ? 'ink' : 'warn'}
            sub={
              mm.end_datetime
                ? `${moment(mm.end_datetime).format('lll')} ${getLocalTimezoneLabel(mm.end_datetime)} · ingested ${
                    mm.last_update ? moment(mm.last_update).fromNow() : 'never'
                  }`
                : 'no measurements published yet'
            }
          />
          {coverage !== null && (
            <Tile label="coverage" className="col-span-2 lg:col-span-1" value={`${coverage}%`} sub={
              mm.start_datetime && mm.end_datetime
                ? `${moment(mm.start_datetime).format('ll')} → ${moment(mm.end_datetime).format('ll')}`
                : null
            }>
              <CoverageBar percent={coverage}/>
            </Tile>
          )}
          <p className="col-span-2 text-[12px] leading-relaxed text-slate-500 lg:col-span-1 dark:text-gray-400">
            Measurements are revised for a few weeks after publication and then
            frozen, so the most recent history can still change.
          </p>
        </aside>
      </div>

      {/* Download bar */}
      <div className="hidden flex-wrap items-center gap-2.5 border-t border-slate-900/[0.06] bg-slate-50/80 px-5 py-2.5 lg:flex dark:border-gray-100/[0.07] dark:bg-gray-100/[0.03]">
        <code className="min-w-0 flex-1 overflow-x-auto whitespace-nowrap font-mono text-[11.5px] text-slate-900 dark:text-white">
          {request}
        </code>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onCopyRequest && onCopyRequest(request); }}
          className="rounded-md border border-slate-300 bg-surface px-2.5 py-1 text-[11px] text-slate-700 hover:border-brand-500 hover:text-brand-600 dark:border-gray-600 dark:bg-gray-870 dark:text-gray-200 dark:hover:text-brand-400"
        >
          Copy request
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onCopyId && onCopyId(r.id); }}
          className="rounded-md border border-slate-300 bg-surface px-2.5 py-1 text-[11px] text-slate-700 hover:border-brand-500 hover:text-brand-600 dark:border-gray-600 dark:bg-gray-870 dark:text-gray-200 dark:hover:text-brand-400"
        >
          Copy ID
        </button>
        <a
          href="/docs/downloading_raw_data/"
          onClick={(e) => e.stopPropagation()}
          className="rounded-md border border-slate-300 bg-surface px-2.5 py-1 text-[11px] text-slate-700 hover:border-brand-500 hover:text-brand-600 dark:border-gray-600 dark:bg-gray-870 dark:text-gray-200 dark:hover:text-brand-400"
        >
          Pagination ↗
        </a>
      </div>
    </div>
  );
}
